import * as React from 'react';
import { useState } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  useRecordContext,
  useGetList,
  useCreate,
  useUpdate,
  useRefresh,
  useNotify,
  ReferenceField,
  NumberField,
  List,
  Datagrid,
  ChipField,
  TopToolbar,
  SearchInput,
  useListContext,
} from 'react-admin';
import { Box, Paper, Typography, TextField as MuiTextField, Button, Chip, Tabs, Tab, Divider } from '@mui/material';

const statusColors = { Open: '#2ecc71', Closed: '#e74c3c' };

// === নতুন: ALL / OPEN / CLOSED ট্যাব ===
// status filter এখন dropdown না, উপরে ট্যাব হিসেবে থাকবে (Withdrawals পেজের মতো)।
const TicketTabbedDatagrid = () => {
  const listContext = useListContext();
  const { filterValues, setFilters, displayedFilters } = listContext;

  const currentTab = filterValues.status ?? 'all';

  const handleChange = (event, value) => {
    const newFilterValues = { ...filterValues };
    if (value === 'all') {
      delete newFilterValues.status;
    } else {
      newFilterValues.status = value;
    }
    setFilters(newFilterValues, displayedFilters);
  };

  return (
    <>
      <Tabs value={currentTab} onChange={handleChange} indicatorColor="primary" textColor="primary">
        <Tab label="ALL" value="all" />
        <Tab label="OPEN" value="Open" />
        <Tab label="CLOSED" value="Closed" />
      </Tabs>
      <Divider />
      <Datagrid rowClick="show">
        <TextField source="ticket_number" label="Ticket Number" />
        <ReferenceField source="agent" reference="agents" label="Agent" link="show">
          <TextField source="display_name" />
        </ReferenceField>
        <TextField source="subject" label="Subject" />
        <ChipField source="status" />
        <ReferenceField source="deposit_request" reference="deposits" label="Related Deposit" link="edit" emptyText="-">
          <NumberField source="amount" />
        </ReferenceField>
        <DateField source="created_at" label="Created at" showTime />
        <DateField source="updated_at" label="Updated at" showTime />
      </Datagrid>
    </>
  );
};

const ticketFilters = [
  <SearchInput source="q" alwaysOn placeholder="সার্চ করুন..." />,
];

const TicketListActions = () => <TopToolbar />;

export const AgentSupportTicketList = (props) => (
  <List
    {...props}
    filters={ticketFilters}
    actions={<TicketListActions />}
    sort={{ field: 'created_at', order: 'DESC' }}
    pagination={false}
    perPage={1000}
  >
    <TicketTabbedDatagrid />
  </List>
);

const StatusToggleButton = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const refresh = useRefresh();
  const notify = useNotify();
  if (!record) return null;
  const nextStatus = record.status === 'Open' ? 'Closed' : 'Open';
  return (
    <Button
      variant="outlined"
      size="small"
      onClick={() =>
        update(
          'agent-support-tickets',
          { id: record.id, data: { status: nextStatus }, previousData: record },
          {
            onSuccess: () => {
              refresh();
              notify(`Ticket marked as ${nextStatus}`);
            },
          }
        )
      }
      disabled={isLoading}
      sx={{ ml: 2 }}
    >
      Mark as {nextStatus}
    </Button>
  );
};

const TicketStatusBadge = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Chip
      label={record.status}
      sx={{ backgroundColor: statusColors[record.status] || '#ccc', color: '#fff', fontWeight: 'bold' }}
    />
  );
};

const ChatBubble = ({ message }) => {
  const isAdmin = message.is_from_admin;
  return (
    <Box sx={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start', mb: 1 }}>
      <Paper
        sx={{
          p: 1.5,
          maxWidth: '70%',
          backgroundColor: isAdmin ? '#1976d2' : '#eeeeee',
          color: isAdmin ? '#fff' : '#000',
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
          {isAdmin ? 'Admin' : 'Agent'} — {new Date(message.created_at).toLocaleString()}
        </Typography>
        {message.text && <Typography variant="body2">{message.text}</Typography>}
        {message.image && (
          <Box mt={1}>
            <img src={message.image} alt="attachment" style={{ maxWidth: '100%', borderRadius: 4 }} />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const TicketChatThread = () => {
  const record = useRecordContext();
  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [create, { isLoading }] = useCreate();
  const refresh = useRefresh();
  const notify = useNotify();

  const { data: messages, isLoading: loadingMessages, refetch } = useGetList(
    'agent-ticket-messages',
    {
      filter: { ticket: record?.id },
      sort: { field: 'created_at', order: 'ASC' },
      pagination: { page: 1, perPage: 200 },
    },
    { enabled: !!record?.id }
  );

  if (!record) return null;

  const handleSend = () => {
    if (!text.trim() && !imageFile) return;
    create(
      'agent-ticket-messages',
      { data: { ticket: record.id, is_from_admin: true, text, image: imageFile } },
      {
        onSuccess: () => {
          setText('');
          setImageFile(null);
          refetch();
          refresh();
        },
        onError: (error) => notify(`Error: ${error.message}`, { type: 'error' }),
      }
    );
  };

  return (
    <Box mt={3} width="100%">
      <Typography variant="h6" gutterBottom>
        Chat Thread
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, maxHeight: 500, overflowY: 'auto', mb: 2 }}>
        {loadingMessages && <Typography>Loading...</Typography>}
        {messages && messages.length === 0 && (
          <Typography color="text.secondary">No messages yet.</Typography>
        )}
        {messages && messages.map((m) => <ChatBubble key={m.id} message={m} />)}
      </Paper>

      <Box display="flex" gap={1} alignItems="flex-start">
        <MuiTextField
          fullWidth
          multiline
          minRows={2}
          placeholder="Write a reply..."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Box display="flex" flexDirection="column" gap={1}>
          <Button variant="outlined" component="label" size="small">
            Attach
            <input hidden type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
          </Button>
          <Button variant="contained" onClick={handleSend} disabled={isLoading}>
            Send
          </Button>
        </Box>
      </Box>
      {imageFile && <Typography variant="caption">Selected: {imageFile.name}</Typography>}
    </Box>
  );
};

export const AgentSupportTicketShow = (props) => (
  <Show {...props} title="Agent Support Ticket">
    <SimpleShowLayout>
      <TextField source="ticket_number" label="Ticket Number" />
      <ReferenceField source="agent" reference="agents" label="Agent" link="show">
        <TextField source="display_name" />
      </ReferenceField>
      <TextField source="subject" label="Subject" />
      <Box display="flex" alignItems="center">
        <TicketStatusBadge label="Status" />
        <StatusToggleButton />
      </Box>
      <ReferenceField source="deposit_request" reference="deposits" label="Related Deposit" link="edit" emptyText="-">
        <NumberField source="amount" />
      </ReferenceField>
      <DateField source="created_at" label="Created at" showTime />
      <DateField source="updated_at" label="Updated at" showTime />
      <TicketChatThread />
    </SimpleShowLayout>
  </Show>
);